import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  ArrowLeft,
  Check,
  CreditCard,
  Smartphone,
  Building2,
  Loader2,
  Shield,
  Zap,
  TrendingUp,
  Crown,
} from 'lucide-react'
import { AdminUser } from '@/lib/authService'
import { openPaystackPopup, isPaystackConfigured, PaystackResponse } from '@/lib/paystackWeb'
import { upgradeFromTrial } from '@/lib/subscriptionService'
import { createReceipt, generateReceiptNumber } from '@/lib/invoiceService'
import { EmailService } from '@/lib/emailService'
import { supabase } from '@/lib/supabase'
import { useToast } from '@/hooks/use-toast'

interface CheckoutProps {
  user: AdminUser | null
}

type BillingCycle = 'monthly' | 'annual' | 'quarterly'
type PlanKey = 'starter' | 'growth' | 'leadership'

interface PlanInfo {
  name: string
  icon: React.ReactNode
  color: string
  pricing: Record<BillingCycle, number>
  features: string[]
}

const PLANS: Record<PlanKey, PlanInfo> = {
  starter: {
    name: 'Starter',
    icon: <Zap className="h-5 w-5" />,
    color: 'from-blue-500 to-cyan-500',
    pricing: {
      monthly: 199,
      annual: 1910,
      quarterly: 597,
    },
    features: ['500 members', '5 admin accounts', '10GB storage', '300 SMS/month'],
  },
  growth: {
    name: 'Growth',
    icon: <TrendingUp className="h-5 w-5" />,
    color: 'from-sky-500 to-cyan-400',
    pricing: {
      monthly: 499,
      annual: 4790,
      quarterly: 1497,
    },
    features: ['2,000 members', 'Unlimited admins', '50GB storage', '1,000 SMS/month'],
  },
  leadership: {
    name: 'Leadership',
    icon: <Crown className="h-5 w-5" />,
    color: 'from-amber-500 to-orange-500',
    pricing: {
      monthly: 1499,
      annual: 14390,
      quarterly: 4497,
    },
    features: ['Unlimited members', 'Unlimited admins', '500GB storage', '3,000 SMS/month'],
  },
}

const BILLING_LABELS: Record<BillingCycle, string> = {
  monthly: 'Monthly',
  annual: 'Annual',
  quarterly: 'Quarterly',
}

export default function Checkout({ user }: CheckoutProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { toast } = useToast()

  const planParam = searchParams.get('plan') as PlanKey | null
  const billingParam = searchParams.get('billing') as BillingCycle | null

  const [selectedPlan, setSelectedPlan] = useState<PlanKey>(planParam && PLANS[planParam] ? planParam : 'growth')
  const [billingCycle, setBillingCycle] = useState<BillingCycle>(billingParam || 'annual')
  const [processing, setProcessing] = useState(false)
  const [churchName, setChurchName] = useState('')
  const [churchEmail, setChurchEmail] = useState('')

  const plan = PLANS[selectedPlan]
  const price = plan.pricing[billingCycle]
  const monthlyEquiv = billingCycle === 'annual'
    ? Math.round(price / 12)
    : billingCycle === 'quarterly'
    ? Math.round(price / 3)
    : price
  const annualSavings = billingCycle === 'annual'
    ? (plan.pricing.monthly * 12) - plan.pricing.annual
    : 0

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }
    loadChurchInfo()
  }, [user])

  async function loadChurchInfo() {
    if (!user?.churchId) return
    const { data } = await supabase
      .from('churches')
      .select('name, email')
      .eq('id', user.churchId)
      .single()

    if (data) {
      setChurchName(data.name || '')
      setChurchEmail(data.email || user.email || '')
    }
  }

  async function handlePaystackPayment() {
    if (!user?.churchId || !user?.email) {
      toast({ title: 'Error', description: 'Please log in to continue.', variant: 'destructive' })
      return
    }

    if (!isPaystackConfigured()) {
      toast({
        title: 'Payment Not Available',
        description: 'Online payment is not configured yet. Please contact support.',
        variant: 'destructive',
      })
      return
    }

    setProcessing(true)

    try {
      await openPaystackPopup({
        email: churchEmail || user.email,
        amount: price,
        churchId: user.churchId,
        churchName,
        plan: selectedPlan,
        billingCycle,
        onSuccess: async (response: PaystackResponse) => {
          await handlePaymentSuccess(response)
        },
        onClose: () => {
          setProcessing(false)
          toast({ title: 'Payment Cancelled', description: 'You closed the payment window.' })
        },
      })
    } catch (error) {
      setProcessing(false)
      toast({
        title: 'Payment Error',
        description: error instanceof Error ? error.message : 'Failed to initialize payment.',
        variant: 'destructive',
      })
    }
  }

  async function handlePaymentSuccess(response: PaystackResponse) {
    try {
      // 1. Upgrade subscription via RPC
      const upgradeResult = await upgradeFromTrial(
        user!.churchId!,
        selectedPlan,
        billingCycle,
        'paystack',
        true
      )

      if (!upgradeResult.success) {
        throw new Error(upgradeResult.error || 'Failed to upgrade subscription')
      }

      // 2. Record payment in subscription_payments
      const receiptNum = generateReceiptNumber()
      await supabase.from('subscription_payments').insert({
        church_id: user!.churchId,
        amount: price,
        currency: 'GHS',
        payment_method: 'paystack',
        transaction_reference: response.reference,
        payment_date: new Date().toISOString().split('T')[0],
        status: 'completed',
        receipt_number: receiptNum,
        notes: `${plan.name} Plan - ${BILLING_LABELS[billingCycle]} subscription via PayStack`,
        recorded_by: user!.id,
      })

      // 3. Generate receipt
      await createReceipt(user!.churchId!, 'paystack', response.reference)

      // 4. Send confirmation email
      await EmailService.sendPaymentConfirmation({
        churchName,
        churchEmail: churchEmail || user!.email,
        adminName: `${user!.firstName} ${user!.lastName}`,
        amount: price,
        currency: 'GHS',
        paymentMethod: 'paystack',
        transactionReference: response.reference,
        paymentDate: new Date().toISOString(),
        receiptNumber: receiptNum,
      })

      setProcessing(false)

      toast({
        title: 'Payment Successful!',
        description: `Your ${plan.name} plan is now active. A receipt has been sent to your email.`,
      })

      // Redirect to dashboard
      navigate('/dashboard?subscription=activated')
    } catch (error) {
      setProcessing(false)
      console.error('Post-payment processing error:', error)
      toast({
        title: 'Payment received but setup failed',
        description: 'Your payment was received. Please contact support if your plan is not activated within a few minutes.',
        variant: 'destructive',
      })
    }
  }

  if (!user) return null

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate('/pricing')} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Pricing
          </Button>
          <h1 className="text-3xl font-bold">Complete Your Subscription</h1>
          <p className="text-muted-foreground mt-1">
            Upgrade {churchName || 'your church'} to unlock more features
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: Plan Selection & Billing */}
          <div className="lg:col-span-2 space-y-6">
            {/* Plan Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Select Plan</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  {(Object.entries(PLANS) as [PlanKey, PlanInfo][]).map(([key, p]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedPlan(key)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        selectedPlan === key
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-md bg-gradient-to-r ${p.color} flex items-center justify-center text-white mb-2`}>
                        {p.icon}
                      </div>
                      <div className="font-semibold">{p.name}</div>
                      <div className="text-sm text-muted-foreground">
                        GHS {p.pricing.monthly}/mo
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Billing Cycle */}
            <Card>
              <CardHeader>
                <CardTitle>Billing Cycle</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(['annual', 'quarterly', 'monthly'] as BillingCycle[]).map((cycle) => {
                    const cyclePrice = plan.pricing[cycle]
                    const isSelected = billingCycle === cycle

                    return (
                      <button
                        key={cycle}
                        onClick={() => setBillingCycle(cycle)}
                        className={`w-full p-4 rounded-lg border-2 text-left transition-all flex items-center justify-between ${
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            isSelected ? 'border-primary' : 'border-gray-300'
                          }`}>
                            {isSelected && <div className="w-3 h-3 rounded-full bg-primary" />}
                          </div>
                          <div>
                            <div className="font-medium">{BILLING_LABELS[cycle]}</div>
                            <div className="text-sm text-muted-foreground">
                              {cycle === 'annual' && 'Billed once per year - Save 20%'}
                              {cycle === 'quarterly' && 'Billed every 3 months'}
                              {cycle === 'monthly' && 'Billed every month'}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold">GHS {cyclePrice.toLocaleString()}</div>
                          {cycle === 'annual' && (
                            <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200">
                              Save GHS {((plan.pricing.monthly * 12) - cyclePrice).toLocaleString()}
                            </Badge>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Payment Method */}
            <Card>
              <CardHeader>
                <CardTitle>Payment</CardTitle>
                <CardDescription>Secure payment via PayStack</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Smartphone className="h-4 w-4" />
                      Mobile Money
                    </div>
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      Card
                    </div>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Bank
                    </div>
                  </div>

                  <Button
                    onClick={handlePaystackPayment}
                    disabled={processing}
                    className="w-full h-12 text-lg bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Shield className="h-5 w-5 mr-2" />
                        Pay GHS {price.toLocaleString()} Now
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    Secured by PayStack. You'll choose your payment method (Mobile Money, Card, or Bank) in the next step.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: Order Summary */}
          <div>
            <Card className="sticky top-8">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Plan */}
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${plan.color} flex items-center justify-center text-white`}>
                    {plan.icon}
                  </div>
                  <div>
                    <div className="font-semibold">{plan.name} Plan</div>
                    <div className="text-sm text-muted-foreground">{BILLING_LABELS[billingCycle]}</div>
                  </div>
                </div>

                <Separator />

                {/* Features */}
                <div className="space-y-2">
                  {plan.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-600" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <Separator />

                {/* Price breakdown */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{plan.name} Plan ({BILLING_LABELS[billingCycle]})</span>
                    <span>GHS {price.toLocaleString()}</span>
                  </div>
                  {annualSavings > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Annual savings</span>
                      <span>-GHS {annualSavings.toLocaleString()}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span>GHS {price.toLocaleString()}</span>
                  </div>
                  {billingCycle !== 'monthly' && (
                    <p className="text-xs text-muted-foreground text-center">
                      Equivalent to GHS {monthlyEquiv.toLocaleString()}/month
                    </p>
                  )}
                </div>

                <Separator />

                {/* Church info */}
                <div className="text-sm text-muted-foreground">
                  <p><strong>Church:</strong> {churchName || 'Loading...'}</p>
                  <p><strong>Email:</strong> {churchEmail || user?.email || 'Loading...'}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
